// src/services/expenseService.js
import apiClient from './apiClient';

/**
 * Expense service for API operations
 */
export const expenseService = {
  /**
   * Get all expenses with optional filters
   * @param {Object} filters - Optional filters (category, dateFrom, dateTo, status, etc.)
   * @returns {Promise<Object>} Response with expenses array
   */
  getAllExpenses: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/expenses?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch expenses');
    }
  },

  /**
   * Get a specific expense by ID
   * @param {string|number} expenseId - The expense ID
   * @returns {Promise<Object>} Response with expense data
   */
  getExpenseById: async (expenseId) => {
    try {
      const response = await apiClient.get(`/expenses/${expenseId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Expense not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch expense');
    }
  },

  /**
   * Create a new expense
   * @param {Object} expenseData - Expense data
   * @returns {Promise<Object>} Response with created expense
   */
  createExpense: async (expenseData) => {
    try {
      const response = await apiClient.post('/expenses', expenseData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create expense');
    }
  },

  /**
   * Update an existing expense
   * @param {string|number} expenseId - The expense ID
   * @param {Object} expenseData - Updated expense data
   * @returns {Promise<Object>} Response with updated expense
   */
  updateExpense: async (expenseId, expenseData) => {
    try {
      const response = await apiClient.put(`/expenses/${expenseId}`, expenseData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update expense');
    }
  },

  /**
   * Delete an expense
   * @param {string|number} expenseId - The expense ID
   * @returns {Promise<Object>} Response confirming deletion
   */
  deleteExpense: async (expenseId) => {
    try {
      const response = await apiClient.delete(`/expenses/${expenseId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete expense');
    }
  },

  /**
   * Upload receipt/document for expense
   * @param {string|number} expenseId - The expense ID
   * @param {File} file - The file to upload
   * @returns {Promise<Object>} Response with upload confirmation
   */
  uploadReceipt: async (expenseId, file) => {
    try {
      const formData = new FormData();
      formData.append('receipt', file);
      
      const response = await apiClient.post(`/expenses/${expenseId}/receipt`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload receipt');
    }
  },

  /**
   * Delete receipt/document for expense
   * @param {string|number} expenseId - The expense ID
   * @param {string} receiptId - The receipt ID to delete
   * @returns {Promise<Object>} Response confirming deletion
   */
  deleteReceipt: async (expenseId, receiptId) => {
    try {
      const response = await apiClient.delete(`/expenses/${expenseId}/receipt/${receiptId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete receipt');
    }
  },

  /**
   * Get expense categories for dropdown
   * @returns {Promise<Array>} List of expense categories
   */
  getExpenseCategories: async () => {
    try {
      const response = await apiClient.get('/expenses/categories');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch expense categories');
    }
  },

  /**
   * Get expense summary for tax reporting
   * @param {Object} filters - Date range and other filters
   * @returns {Promise<Object>} Expense summary by category
   */
  getExpenseSummary: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Transform dateFrom/dateTo to backend's expected format if needed
      const queryParams = { ...filters };
      if (filters.dateFrom) {
        queryParams.startDate = filters.dateFrom;
      }
      if (filters.dateTo) {
        queryParams.endDate = filters.dateTo;
      }
      
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/expenses/summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching expense summary:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch expense summary');
    }
  },

  /**
   * Export expenses for tax reporting
   * @param {Object} filters - Export filters and format
   * @returns {Promise<Blob>} Export file
   */
  exportExpenses: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/expenses/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export expenses');
    }
  }
};
